import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import type { Queue } from '../types';

/**
 * Очереди тенанта — первый экран трекера. Задача не существует вне очереди, поэтому работа
 * начинается здесь: создать очередь, зайти в неё, завести задачи.
 */
export function QueuesPage() {
  const { tenantId = '' } = useParams();
  const catalog = useTenantCatalog(tenantId);
  const [creating, setCreating] = useState(false);

  const active = catalog.queues.filter((queue) => !queue.archived);
  const archived = catalog.queues.filter((queue) => queue.archived);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Очереди</h1>
          <div className="muted small">
            {catalog.isLoading ? ' ' : `Всего: ${catalog.queues.length}`}
          </div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Icon name="plus" />
          Новая очередь
        </button>
      </div>

      {catalog.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : catalog.queues.length === 0 ? (
        <div className="card">
          <div className="state-title">Очередей пока нет</div>
          <p className="muted">
            Очередь — это проект или направление работы. Она задаёт ключ задач (например,
            <code> TRK-15</code>) и собственный набор статусов, поэтому первой создаётся именно она.
          </p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              Создать очередь
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="queue-grid">
            {active.map((queue) => (
              <QueueCard key={queue.id} tenantId={tenantId} queue={queue} />
            ))}
          </div>

          {archived.length > 0 && (
            <>
              <h2 style={{ marginTop: '1.5rem' }}>Архив</h2>
              <div className="queue-grid">
                {archived.map((queue) => (
                  <QueueCard key={queue.id} tenantId={tenantId} queue={queue} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {creating && <CreateQueueModal tenantId={tenantId} onClose={() => setCreating(false)} />}
    </>
  );
}

function QueueCard({ tenantId, queue }: { tenantId: string; queue: Queue }) {
  return (
    <Link className="queue-card" to={`/t/${tenantId}/queues/${queue.id}`}>
      <div className="queue-key">{queue.key}</div>
      <div className="queue-name">
        {queue.name}
        {queue.archived && <span className="badge badge-gray" style={{ marginLeft: 6 }}>архив</span>}
      </div>
      <div className="queue-meta">
        {queue.issueCounter > 0 ? `Задач заведено: ${queue.issueCounter}` : 'Задач ещё нет'}
      </div>
    </Link>
  );
}

/**
 * Ключ очереди задаётся руками и потом не меняется: он попадает в ключ каждой задачи, а те живут
 * в ссылках, письмах и коммитах. Поэтому подсказка про заглавные латинские буквы — не придирка.
 */
export function CreateQueueModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated?: (queue: Queue) => void;
}) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => api.post<Queue>(`/tenants/${tenantId}/queues`, { key, name }),
    onSuccess: async (queue) => {
      await queryClient.invalidateQueries({ queryKey: ['queues', tenantId] });
      onCreated?.(queue);
      onClose();
    },
  });

  return (
    <Modal title="Новая очередь" onClose={onClose}>
      <div className="form-group">
        <label className="form-label" htmlFor="queue-key">
          Ключ
        </label>
        <input
          id="queue-key"
          className="input"
          value={key}
          onChange={(event) => setKey(event.target.value.toUpperCase())}
          placeholder="TRK"
          autoFocus
        />
        <div className="form-hint">
          Заглавные латинские буквы. Из него складывается ключ задачи — TRK-1, TRK-2. Потом его
          не изменить.
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="queue-name">
          Название
        </label>
        <input
          id="queue-name"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Разработка трекера"
        />
      </div>

      {create.isError && <div className="form-error">{(create.error as ApiError).message}</div>}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>
          Отмена
        </button>
        <button
          className="btn btn-primary"
          disabled={!key.trim() || !name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          Создать
        </button>
      </div>
    </Modal>
  );
}
